// 261012 E1-P3 — ô "Nhận dạng hình ảnh" của một dòng (nhân vật HOẶC bối cảnh, theo `kind`). Chỉ VẼ: mọi quyết định bật/tắt nút,
// nhãn, thẻ nằm ở `visualIdentityView.ts` (đã có test). Ghi / chạy AI đi qua `useVisualIdentity` do EntityTable truyền xuống.
import React, { useState } from 'react';
import { Input, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import Button from './WbButton';
import type { VisualEntityKind, VisualProfileView } from '../../../types/adaptation';
import type { VisualEntityRef, VisualIdentityApi } from './useVisualIdentity';
import {
  baseVersionOf, cellState, editValidation, previewBlockLine, VisualAction, VisualCtx, VisualTone,
} from './visualIdentityView';

const { Text, Paragraph } = Typography;

const TONE_COLOR: Record<VisualTone, string | undefined> = { ai: 'blue', user: 'green', keep: 'purple', warn: 'orange', mut: undefined };
const tagStyle: React.CSSProperties = { fontSize: 11, lineHeight: '16px', margin: '0 4px 2px 0', padding: '0 6px' };

interface Props {
  kind: VisualEntityKind;
  id: string;
  /** Tên hiển thị (tên trong phim, rơi về tên nguồn) — nhãn dòng khối + hộp hỏi. */
  label: string;
  view: VisualProfileView | null;
  ctx: VisualCtx;
  api: Pick<VisualIdentityApi, 'maxChars' | 'reload' | 'requestRun' | 'saveText' | 'setLock' | 'requestClear'>;
}

const VisualIdentityCell: React.FC<Props> = ({ kind, id, label, view, ctx, api }) => {
  const [editing, setEditing] = useState<{ draft: string; baseVersion: string } | null>(null);
  const st = cellState(view, ctx, kind);
  const ref: VisualEntityRef = { kind, id, label };

  /** Nút có thể khoá: bọc `span` để Tooltip lý do vẫn hiện khi nút `disabled`. */
  const guard = (action: VisualAction, node: React.ReactElement) => {
    const why = st.actionReason[action];
    return why ? <Tooltip title={why}><span>{node}</span></Tooltip> : node;
  };

  if (st.phase === 'loading') return <Text type="secondary">…</Text>;
  if (st.phase === 'failed') {
    return (
      <Space size={4}>
        <Text type="secondary">{st.label}</Text>
        <Button size="small" type="link" onClick={api.reload}>Thử lại</Button>
      </Space>
    );
  }

  if (editing) {
    const v = editValidation(editing.draft, api.maxChars);
    const preview = previewBlockLine(kind, label, editing.draft);
    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text strong style={{ fontSize: 12 }}>Đang sửa</Text>
        <Input.TextArea
          rows={3} value={editing.draft} status={v.over ? 'error' : undefined} autoFocus
          onChange={(e) => setEditing((s) => (s ? { ...s, draft: e.target.value } : s))}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <Text type={v.over ? 'danger' : 'secondary'} style={{ fontSize: 11 }}>
            Một dòng · {v.count}{v.max !== null ? `/${v.max}` : ''} ký tự · không chứa tên gốc (nếu có sẽ báo lỗi)
          </Text>
          {preview && <Text type="secondary" style={{ fontSize: 11 }}>Film sẽ đọc: <b>{preview}</b></Text>}
        </div>
        <Space size={6}>
          {guard('edit', (
            <Button
              size="small" type="primary" disabled={!st.canEdit || !v.ok}
              onClick={async () => {
                const close = await api.saveText(ref, v.normalized, editing.baseVersion);
                if (close) setEditing(null);
              }}
            >Lưu (thành “Đã sửa tay” + Giữ)</Button>
          ))}
          <Button size="small" onClick={() => setEditing(null)}>Huỷ</Button>
          {v.message && <Text type="secondary" style={{ fontSize: 11 }}>{v.message}</Text>}
        </Space>
      </Space>
    );
  }

  const openEditor = () => setEditing({ draft: st.text, baseVersion: baseVersionOf(view) });

  if (st.phase === 'empty') {
    return (
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        <Tag style={tagStyle}>{st.label}</Tag>
        {st.hint && <Text type="secondary" style={{ fontSize: 11 }}>{st.hint}</Text>}
        <Space size={6} style={{ marginTop: 2 }}>
          {guard('propose', (
            <Button size="small" type="primary" disabled={!st.canPropose}
              onClick={() => api.requestRun('one', { ...ref, hasProfile: false })}>Đề xuất</Button>
          ))}
          {guard('edit', <Button size="small" disabled={!st.canEdit} onClick={openEditor}>Tự viết</Button>)}
        </Space>
      </Space>
    );
  }

  const version = baseVersionOf(view);
  return (
    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2, tooltip: st.text }}>{st.text}</Paragraph>
      <div>
        {st.tags.map((t) => <Tag key={t.key} color={TONE_COLOR[t.tone]} style={tagStyle}>{t.label}</Tag>)}
      </div>
      {st.notes.map((n) => <Text key={n} type="secondary" style={{ fontSize: 11 }}>{n}</Text>)}
      {st.blockLine && (
        <Text type="secondary" style={{ fontSize: 11 }} ellipsis={{ tooltip: st.blockLine }}>Film sẽ đọc: <b>{st.blockLine}</b></Text>
      )}
      <Space size={6} wrap style={{ marginTop: 2 }}>
        {guard('edit', <Button size="small" disabled={!st.canEdit} onClick={openEditor}>Sửa</Button>)}
        {guard('redo', (
          <Button size="small" disabled={!st.canRedo} onClick={() => api.requestRun('one', { ...ref, hasProfile: true })}>Đề xuất lại</Button>
        ))}
        {guard('lock', (
          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>Giữ</Text>
            <Switch size="small" checked={!!view?.locked} disabled={!st.canLock} onChange={(locked) => { void api.setLock(ref, locked, version); }} />
          </Space>
        ))}
        {guard('clear', <Button size="small" danger disabled={!st.canClear} onClick={() => api.requestClear(ref, version)}>Xoá</Button>)}
      </Space>
    </Space>
  );
};

export default VisualIdentityCell;
